import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Plus } from "lucide-react";
import { featuredServices, services } from "../../data/services";

function ServiceRow({ service, index, featured = false }) {
  const [open, setOpen] = useState(false);

  const closeWhenFocusLeaves = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.45, delay: Math.min(index * 0.05, 0.25) }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocusCapture={() => setOpen(true)}
      onBlurCapture={closeWhenFocusLeaves}
      className={`relative overflow-hidden border-b transition-colors duration-300 ${
        open
          ? "border-[#00844A] bg-[#00844A] text-white"
          : "border-zinc-200 bg-white text-zinc-950"
      }`}
      data-testid={`service-row-${service.slug}`}
    >
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-controls={`service-summary-${service.slug}`}
        className="relative z-10 w-full flex items-center gap-4 sm:gap-8 px-5 sm:px-8 py-6 sm:py-7 text-left"
      >
        <span
          className={`w-14 sm:w-20 shrink-0 text-[10px] sm:text-xs font-mono uppercase tracking-wider ${
            open ? "text-white/60" : "text-zinc-400"
          }`}
        >
          {featured ? `0${index + 1}` : service.number}
        </span>
        <h3 className="flex-1 font-display font-bold text-xl sm:text-2xl lg:text-3xl tracking-tight">
          {service.name}
        </h3>
        {featured && (
          <span
            className={`hidden sm:inline-flex px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ${
              open ? "bg-white text-[#00844A]" : "bg-[#00844A] text-white"
            }`}
          >
            Öne Çıkan
          </span>
        )}
        <Plus
          size={22}
          aria-hidden="true"
          className={`shrink-0 transition-transform duration-300 ${open ? "rotate-45" : ""}`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={`service-summary-${service.slug}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="relative z-10 overflow-hidden"
          >
            <div className="grid grid-cols-1 md:grid-cols-12 gap-5 px-5 sm:px-8 pb-7 sm:pb-8">
              <div className="hidden md:block md:col-span-1" />
              <p className="md:col-span-7 text-sm sm:text-base leading-relaxed text-white/85 max-w-2xl">
                {service.tagline}. {service.intro}
              </p>
              <div className="md:col-span-4 md:flex md:justify-end md:items-start">
                <Link
                  to={`/hizmetler/${service.slug}`}
                  className="group inline-flex items-center gap-3 px-5 py-3 bg-white text-zinc-950 text-sm font-bold hover:bg-zinc-950 hover:text-white transition-colors"
                  data-testid={`service-details-${service.slug}`}
                >
                  Detayları Gör
                  <ArrowUpRight
                    size={16}
                    className="group-hover:rotate-45 transition-transform duration-300"
                  />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
}

export const Services = () => {
  return (
    <section
      id="hizmetler"
      data-testid="services-section"
      className="scroll-mt-24 sm:scroll-mt-28 py-24 sm:py-32 bg-white relative"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-5">
            <div className="text-xs tracking-[0.25em] uppercase font-bold text-zinc-500 mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-zinc-950" />
              02 — Hizmetler
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="font-display font-black text-zinc-950 text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]"
            >
              İhtiyacınız olan <br />
              hizmete <br />
              <span className="italic font-medium text-zinc-400">kolayca ulaşın.</span>
            </motion.h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-base sm:text-lg text-zinc-600 leading-relaxed">
              Hizmet başlığının üzerine gelerek kısa açıklamayı görebilir, telefonda
              başlığa dokunarak açabilirsiniz. Ayrıntılı kapsam ve sık sorulan sorular
              için “Detayları Gör” bağlantısını kullanın.
            </p>
          </div>
        </div>

        <div className="mb-14 sm:mb-16">
          <div className="flex items-center justify-between gap-4 mb-5">
            <h3 className="font-display font-bold text-xl sm:text-2xl text-zinc-950 tracking-tight">
              Öne Çıkan Hizmetler
            </h3>
            <span className="text-[10px] sm:text-xs uppercase tracking-[0.2em] font-bold text-[#00844A]">
              Hızlı Erişim
            </span>
          </div>
          <div className="border-t border-zinc-200">
            {featuredServices.map((service, index) => (
              <ServiceRow key={service.slug} service={service} index={index} featured />
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-display font-bold text-xl sm:text-2xl text-zinc-950 tracking-tight mb-5">
            Tüm Hizmetler
          </h3>
          <div className="border-t border-zinc-200">
            {services.map((service, index) => (
              <ServiceRow key={service.slug} service={service} index={index} />
            ))}
          </div>
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-6 sm:p-8 bg-zinc-50">
          <div className="font-display font-bold text-lg sm:text-xl text-zinc-950 tracking-tight">
            Listede göremediğiniz bir uygulama var mı? Birlikte planlayalım.
          </div>
          <a
            href="#iletisim"
            data-testid="services-cta-contact"
            className="inline-flex items-center gap-3 px-5 py-3 bg-zinc-950 text-white text-sm font-bold hover:bg-[#00844A] hover:text-white transition-colors"
          >
            İletişime Geç →
          </a>
        </div>
      </div>
    </section>
  );
};
